"use client";

import { c as _c } from "react/compiler-runtime";
import { IconLayoutDashboard, IconLogin, IconMenu2, IconMoon, IconSun } from "@tabler/icons-react";
import Link from "next/link";
import { AppHeaderSeparator } from "@/components/layout/app-header-separator";
import { UserMenu } from "@/components/layout/user-menu";
import { NotificationsPopover } from "@/components/notifications/notifications-popover";
import { useIsClient } from "@/hooks/use-is-client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/hooks/use-theme";
import { useSession } from "@domainstack/auth/client";
import { Button } from "@domainstack/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@domainstack/ui/dropdown-menu";
import { Skeleton } from "@domainstack/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";

/**
 * Session-dependent header buttons that conditionally render based on auth state.
 * Client component that uses useSession hook to avoid turning pages into PPR.
 */
export function AppHeaderClientButtons() {
  const $ = _c(27);
  const {
    data: session,
    isPending
  } = useSession();
  const isMobile = useIsMobile();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const mounted = useIsClient();
  if (!mounted || isPending) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><Skeleton className="size-8 rounded-md max-md:ml-1" /><AppHeaderSeparator className="hidden md:block" /><Skeleton className="hidden h-8 w-[4.75rem] rounded-md md:block" /></>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (session?.user) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><Tooltip><TooltipTrigger render={<Button variant="ghost" size="icon-sm" nativeButton={false} render={<Link href="/dashboard"><IconLayoutDashboard /><span className="sr-only">Dashboard</span></Link>} />} /><TooltipContent>Dashboard</TooltipContent></Tooltip><AppHeaderSeparator /><NotificationsPopover /><AppHeaderSeparator className="mr-2" /><UserMenu /></>;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (isMobile) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" aria-label="Menu"><IconMenu2 /></Button>} />;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== theme) {
      t1 = theme === "dark" ? <IconSun /> : <IconMoon />;
      $[3] = theme;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    const t2 = theme === "dark" ? "Light mode" : "Dark mode";
    let t3;
    if ($[5] !== t1 || $[6] !== t2 || $[7] !== toggleTheme) {
      t3 = <DropdownMenuItem onClick={toggleTheme} closeOnClick={false}>{t1}{t2}</DropdownMenuItem>;
      $[5] = t1;
      $[6] = t2;
      $[7] = toggleTheme;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <DropdownMenuSeparator />;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <DropdownMenuItem nativeButton={false} render={<Link href="/login" scroll={false}><IconLogin />Sign In</Link>} />;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== t3) {
      t6 = <DropdownMenu>{t0}<DropdownMenuContent align="end" className="min-w-48">{t3}{t4}{t5}</DropdownMenuContent></DropdownMenu>;
      $[11] = t3;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t0;
  let t1;
  let t2;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconSun className="scale-100 rotate-0 transition-[transform,opacity] dark:scale-95 dark:-rotate-90 dark:opacity-0" />;
    t1 = <IconMoon className="absolute scale-95 rotate-90 opacity-0 transition-[transform,opacity] dark:scale-100 dark:rotate-0 dark:opacity-100" />;
    t2 = <span className="sr-only">Toggle theme</span>;
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
  } else {
    t0 = $[13];
    t1 = $[14];
    t2 = $[15];
  }
  let t3;
  if ($[16] !== toggleTheme) {
    t3 = <TooltipTrigger render={<Button aria-label="Toggle theme" variant="ghost" size="sm" onClick={toggleTheme}>{t0}{t1}{t2}</Button>} />;
    $[16] = toggleTheme;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const t4 = theme === "dark" ? "Dark mode" : "Light mode";
  let t5;
  if ($[18] !== t4) {
    t5 = <TooltipContent>{t4}</TooltipContent>;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t5) {
    t6 = <Tooltip>{t3}{t5}</Tooltip>;
    $[20] = t3;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AppHeaderSeparator />;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button variant="ghost" size="sm" nativeButton={false} aria-label="Sign In" render={<Link href="/login" scroll={false}>Sign In</Link>} />;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t6) {
    t9 = <>{t6}{t7}{t8}</>;
    $[25] = t6;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}